import React, { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

/**
 * Converts HTML content to plain text while preserving formatting structure.
 */
const htmlToText = (html) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.innerText || temp.textContent || '';
};

/**
 * Converts computed styles to inline styles for clipboard-friendly HTML.
 * Preserves formatting like bold, tables, spacing when pasting into Word/Docs.
 */
const convertStylesToInline = (sourceElement, targetElement) => {
  const computed = window.getComputedStyle(sourceElement);

  // Key styles to preserve for rich text pasting
  const stylesToCopy = [
    'font-weight',
    'font-style',
    'text-decoration',
    'font-size',
    'text-align',
    'padding',
    'margin',
    'border',
    'border-collapse',
    'vertical-align',
  ];

  const inlineStyles = [];
  stylesToCopy.forEach(prop => {
    const value = computed.getPropertyValue(prop);
    if (value && value !== 'normal' && value !== 'none' && value !== '0px' && value !== 'start') {
      // Normalize font-weight to bold for compatibility
      if (prop === 'font-weight' && (value === '700' || value === 'bold' || parseInt(value) >= 600)) {
        inlineStyles.push('font-weight: bold');
      } else if (prop === 'font-style' && value === 'italic') {
        inlineStyles.push('font-style: italic');
      } else if (prop === 'text-decoration' && value.includes('underline')) {
        inlineStyles.push('text-decoration: underline');
      } else if (prop === 'text-align' && value !== 'start' && value !== 'left') {
        inlineStyles.push(`text-align: ${value}`);
      } else if (prop === 'border-collapse') {
        inlineStyles.push(`border-collapse: ${value}`);
      } else if (prop === 'vertical-align' && value !== 'baseline') {
        inlineStyles.push(`vertical-align: ${value}`);
      }
    }
  });

  if (inlineStyles.length > 0) {
    targetElement.setAttribute('style', inlineStyles.join('; '));
  }
};

/**
 * Cleans HTML for clipboard - converts Tailwind classes to inline styles.
 * This ensures bold, italic, lists, tables etc. work when pasting into Word/Docs.
 */
const cleanHtmlForClipboard = (html, sourceContainer = null) => {
  if (!html) return '';

  const temp = document.createElement('div');
  temp.innerHTML = html;

  // If we have access to the source container, convert computed styles to inline
  if (sourceContainer) {
    const sourceElements = sourceContainer.querySelectorAll('*');
    const tempElements = temp.querySelectorAll('*');

    // Map source elements to temp elements and copy styles
    sourceElements.forEach((srcEl, idx) => {
      if (tempElements[idx]) {
        convertStylesToInline(srcEl, tempElements[idx]);
        tempElements[idx].removeAttribute('class');
      }
    });
  } else {
    // Fallback: just remove classes but try to preserve semantic meaning
    const allElements = temp.querySelectorAll('*');
    allElements.forEach(el => {
      el.removeAttribute('class');
      el.removeAttribute('style');
    });
  }

  // Add basic table styling for better Word/Docs compatibility
  const tables = temp.querySelectorAll('table');
  tables.forEach(table => {
    const existingStyle = table.getAttribute('style') || '';
    table.setAttribute('style', `${existingStyle}; border-collapse: collapse; width: 100%;`.replace(/^; /, ''));
  });

  const cells = temp.querySelectorAll('th, td');
  cells.forEach(cell => {
    const existingStyle = cell.getAttribute('style') || '';
    const isHeader = cell.tagName === 'TH';
    const baseStyle = 'border: 1px solid #ccc; padding: 8px;';
    const headerStyle = isHeader ? ' font-weight: bold; background-color: #f5f5f5;' : '';
    cell.setAttribute('style', `${existingStyle}; ${baseStyle}${headerStyle}`.replace(/^; /, ''));
  });

  return temp.innerHTML;
};

/**
 * Fallback rich copy using execCommand (better browser support for HTML)
 */
const fallbackRichCopy = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  const range = document.createRange();
  range.selectNodeContents(container);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);

  try {
    document.execCommand('copy');
    return true;
  } catch (err) {
    console.error('Fallback rich copy failed:', err);
    return false;
  } finally {
    selection.removeAllRanges();
    document.body.removeChild(container);
  }
};

/**
 * Animated checkmark with circular reveal effect
 */
const AnimatedCheckmark = () => {
  const [animate, setAnimate] = React.useState(false);

  React.useEffect(() => {
    requestAnimationFrame(() => setAnimate(true));
  }, []);

  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="#22c55e"
        style={{
          transform: animate ? 'scale(1)' : 'scale(0)',
          opacity: animate ? 1 : 0,
          transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
          transformOrigin: 'center',
        }}
      />
      <path
        d="M7 13l3 3 7-7"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        style={{
          strokeDasharray: 20,
          strokeDashoffset: animate ? 0 : 20,
          transition: 'stroke-dashoffset 0.3s ease-out 0.2s',
        }}
      />
    </svg>
  );
};

/**
 * A button that copies message content to the clipboard.
 * Preserves formatting (bold, lists, etc.) when pasting into rich text apps.
 *
 * @param {string} content - HTML or plain text content to copy
 * @param {boolean} isHtml - Whether content is HTML (default: false)
 * @param {string} className - Additional CSS classes
 * @param {React.RefObject} containerRef - Optional ref to DOM element to copy from (captures full rendered content including widgets)
 */
const CopyMessageButton = ({ content, isHtml = false, className = '', containerRef }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      // If containerRef is provided, get content from the DOM element
      // This captures the full rendered content including widgets
      const sourceContainer = containerRef?.current || null;
      const htmlContent = sourceContainer ? sourceContainer.innerHTML : content;
      const useHtml = sourceContainer ? true : isHtml;

      const plainText = useHtml ? htmlToText(htmlContent) : htmlContent;
      if (!plainText) return;

      let success = false;

      // For HTML content, try to copy with formatting preserved
      if (useHtml && htmlContent) {
        // Pass source container so we can convert computed styles to inline
        const cleanedHtml = cleanHtmlForClipboard(htmlContent, sourceContainer);

        // Try modern ClipboardItem API first
        if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
          try {
            const clipboardItem = new ClipboardItem({
              'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
              'text/plain': new Blob([plainText], { type: 'text/plain' }),
            });
            await navigator.clipboard.write([clipboardItem]);
            success = true;
          } catch (err) {
            console.warn('ClipboardItem failed:', err);
          }
        }

        // Fallback to execCommand
        if (!success) {
          success = fallbackRichCopy(cleanedHtml);
        }
      }

      // Final fallback to plain text
      if (!success) {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(plainText);
          success = true;
        }
      }

      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`p-1.5 rounded-md text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-bg-secondary transition-colors ${className}`}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <AnimatedCheckmark />
      ) : (
        <ClipboardDocumentIcon className="h-4 w-4" />
      )}
    </button>
  );
};

export default CopyMessageButton;
